import { ReactComponent as BaseRemoveIcon } from '@ubeya/shared-web/assets/close-small.svg';
import {
  CircleIcon as BaseCircleIcon,
  TextAreaField as BaseTextAreaField,
  FlexColumn,
  Accordion as BaseAccordion,
  Label,
  Header3SemiBold,
  Body,
  AttentionBox as AttentionBoxBase,
  FlexSpace,
  Flex
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import { required } from '@ubeya/shared/utils/form';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { ALPHA_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import { getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { EMPLOYEE_FIELD_BIRTHDAY } from '@ubeya/shared-web/utils/employee/constants';
import { ClientSelectField, LocationSelectField } from 'shared/components';
import BaseLabelsSelectField from 'shared/components/AddNewSelectors/LabelsSelectField';
import PositionsSelectField from 'shared/components/AddNewSelectors/PositionsSelectField';
import BaseUserCard from '../../ManageBooking/UserCard';
import AssignToField from './AssignToField';
import HourlyBookingGeneralTimeFields from './HourlyBookingGeneralTimeFields';
import HourlyCopyDay from './HourlyCopyDay';
import EmployeeBookingWarningsRenderer from 'scheduling/components/ManageBooking/EmployeeBookingWarningsRenderer';
import CostCenterSection from 'scheduling/components/ShiftsModal/CostCenterSection';
import { getEmployeeAgeInfo } from 'scheduling/utils';
import { OpenShiftsSectionField } from 'scheduling/components/WeekView/Modals/Fields/OpenShiftsSectionField';
import useClientAndLocationSelectionLogic from 'scheduling/hooks/useClientAndLocationSelectionLogic';
import BreaksTable from 'timeSheet/components/ShiftDetails/BreaksTable';
import { ShiftTagsField } from 'scheduling/components/ShiftTags';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
`;

const CircleIcon = styled(BaseCircleIcon)``;

const TextAreaField = styled(BaseTextAreaField)`
  textarea {
    border-radius: 4px;
  }
`;

const GridItem = styled(FlexColumn)`
  gap: 16px;
`;

const Options = styled(Flex)`
  flex-wrap: wrap;
  gap: 16px;
`;

const UserCard = styled(BaseUserCard)`
  border: 1px solid ${({ theme }) => theme.colors.grey2};
  border-radius: 4px;
`;

const OpenShiftsSectionFieldWrapper = styled.div`
  flex: 0 0 calc(50% - 8px);
`;

const PrimaryLabel = styled.div`
  color: ${({ $color, theme }) => $color || theme.colors.primary};
`;

const Wrapper = styled(FlexColumn)``;

const AttentionBoxContent = styled(FlexSpace)`
  width: 100%;
`;

const InfoText = styled(Body)`
  color: ${({ theme }) => theme.colors.black};
  flex: 1;
`;

const SeeEventLink = styled.div`
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }
`;

const AttentionBox = styled(AttentionBoxBase)`
  margin-bottom: 16px;
`;

const FieldSection = styled(FlexColumn)`
  gap: 16px;
  margin-bottom: ${({ $isCreateModalOpen }) => ($isCreateModalOpen ? '32px' : '0')};
`;

const HourlyCopyDayWrapper = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const BreaksTableWrapper = styled.div`
  margin-top: 16px;
`;

const AssigningSectionWrapper = styled(Flex)`
  gap: 16px;
  margin-top: 16px;
`;

const UserCardWrapper = styled.div`
  position: relative;
  .booking-warnings-error-icon {
    position: absolute;
    right: -25px;
    top: 50%;
    transform: translateY(-50%);
    width: 17px;
    height: 17px;
  }
`;

const ClientSelectWrapper = styled(Flex)`
  margin-top: 16px;
  gap: 16px;
  width: 100%;

  & > div {
    flex: 1;
    max-width: calc(50% - 8px);
  }
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.black};
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const NewShiftSection = styled(FlexColumn)`
  .booking-accordion {
    gap: 16px;
  }
`;

const Accordion = styled(BaseAccordion)`
  margin-top: 16px;

  .accordion-details-container {
    margin-top: 16px;
    width: 100%;
  }
`;

const RemoveIcon = styled(BaseRemoveIcon)`
  g {
    stroke: ${({ theme }) => theme.colors.disabled};
  }
`;

const SemiBoldTitle = styled(Header3SemiBold)`
  margin-bottom: 16px;
`;

const LabelsSelectField = styled(BaseLabelsSelectField)``;

const PositionsField = styled(PositionsSelectField)``;

const BreaksEmptyState = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black};
`;

const OpenShiftsSectionComponent = ({
  isCreateModalOpen,
  openShiftsByDayOptions = [],
  selectedOpenShiftId,
  onSelectOpenShift,
  openShiftsByID = {},
  showLabel
}) => {
  const { t } = useContext(ConfigContext);
  return (
    <div>
      <SectionTitle>{t('openShiftsToFill')}:</SectionTitle>

      <FieldSection $isCreateModalOpen={isCreateModalOpen}>
        <Options>
          {openShiftsByDayOptions.map(({ value }, index) => (
            <OpenShiftsSectionFieldWrapper key={index}>
              <OpenShiftsSectionField
                onOpenShiftClick={() => onSelectOpenShift(value)}
                shift={openShiftsByID[value]}
                hideContextOptions
                selected={selectedOpenShiftId === value}
                showLabel={showLabel}
              />
            </OpenShiftsSectionFieldWrapper>
          ))}
        </Options>
      </FieldSection>
    </div>
  );
};

const HourlyBookingFields = ({
  employee,
  t,
  setSelectedDays,
  initialTimingValues,
  selectedDays,
  setEmployee,
  relevantOpenShifts: openShiftsByCurrentDate,
  disableTimesChange,
  timePickerOptions,
  setAddEmployeeModal,
  showLabelField,
  mappedLabels,
  timeFormat,
  enableCopyShift,
  editMode,
  errors,
  startDate,
  endDate,
  branchId,
  shiftId,
  isPositionRequired,
  isWeeklyPlanner,
  projectId,
  isCreateModalOpen,
  setIsCreateModalOpen,
  showLabel,
  bookedOutsideAvailability = false,
  slotWarnings = [],
  employeeWarnings,
  showShiftTags = false,
  eventTags
}) => {
  const { isBlockDemographicFiltersInSchedulingEnabled } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedBranches } = useBranches();
  const isCostCenterRequired = !!mappedBranches?.[branchId]?.scheduleConfig?.isCostCenterRequired;

  const handleSeeEvent = useCallback(() => {
    if (accountId && branchId && projectId) {
      window.open(`/${accountId}/scheduling/branch/${branchId}/project/${projectId}`, '_blank');
    }
  }, [accountId, branchId, projectId]);

  const isAlphaAccount = [1, ALPHA_ACCOUNT_ID, 7, 164].includes(accountId);
  const assignedToLabel = `${t('assignedTo')}${isAlphaAccount ? ` (${t('sortedBySeniority')})` : ''}`;

  // employeeWarnings is set when user picks a new employee in this session and takes over as the
  // live source of truth. Otherwise fall back to the pre-existing booking state from the parent.
  const activeSlotWarnings = employeeWarnings != null ? employeeWarnings.slotWarnings || [] : slotWarnings;
  const activeExternalWarnings =
    employeeWarnings != null
      ? employeeWarnings.explanationMarkWarnings || []
      : bookedOutsideAvailability
      ? [{ type: 'bookedOutsideAvailability', format: t('bookedOutsideAvailability') }]
      : [];
  const { age: employeeAge } = useMemo(() => {
    const birthdateStr = employee?.fields?.[EMPLOYEE_FIELD_BIRTHDAY];
    const result = getEmployeeAgeInfo(birthdateStr, { isAgeHidden: isBlockDemographicFiltersInSchedulingEnabled });

    return result;
  }, [isBlockDemographicFiltersInSchedulingEnabled, employee?.fields]);

  // TODO
  const isClientRequired = false;
  const disableEditingExistingClient = editMode;

  const {
    input: { value: clientBranchIdValue, onChange: setClientBranchId }
  } = useField('clientBranchId');

  const {
    input: { value: locationId, onChange: setLocationId }
  } = useField('locationId');

  const {
    input: { value: areaId, onChange: setAreaId }
  } = useField('areaId');

  const setLocationIdWithAreaReset = useCallback(
    (value) => {
      setAreaId(null);
      setLocationId(value);
    },
    [setAreaId, setLocationId]
  );

  const {
    // showClientField,
    // exists crm,
    // onAddClient,
    onChangeClientBranchId,
    isLoadingLocationField,
    customLocationsOptions,
    onChangeLocation,
    locationErrors,
    getLocationOptionSpecificStyle,
    checkCloseUponLocationOptionSelectFunc
  } = useClientAndLocationSelectionLogic({
    isEdit: editMode,
    clientBranchIdValue,
    setLocationId: setLocationIdWithAreaReset,
    setClientBranchId,
    locationIdValue: locationId,
    locationErrors: errors
    // defaultProjectGeneralComment
    //publicComments
    //onChangePublicComments
  });

  const {
    isLoadingLocationField: isLoadingAreaField,
    customLocationsOptions: areaLocationsOptions,
    onChangeLocation: onChangeArea,
    getLocationOptionSpecificStyle: getAreaOptionSpecificStyle,
    checkCloseUponLocationOptionSelectFunc: checkCloseUponAreaOptionSelectFunc
  } = useClientAndLocationSelectionLogic({
    clientBranchIdValue,
    showSubLocations: true,
    locationIdValue: areaId,
    projectLocationIdValue: locationId,
    setLocationId: setAreaId
  });

  const areaDisplayValue = areaId === locationId && editMode ? null : areaId;

  const {
    input: { onChange: setClientAdminId }
  } = useField('clientAdminId');

  const {
    input: { onChange: setPositionId, value: selectedPositionId }
  } = useField('positionId');

  const {
    input: { value: startTimeValue, onChange: onChangeStartTime }
  } = useField('startTime');

  const {
    input: { value: endTimeValue, onChange: onChangeEndTime }
  } = useField('endTime');

  const {
    input: { onChange: onChangeLabelId }
  } = useField('labelId');

  const {
    input: { value: dateValue }
  } = useField('date');

  const {
    input: { value: clockInValue, onChange: onChangeClockIn }
  } = useField('clockIn');

  const {
    input: { value: clockOutValue, onChange: onChangeClockOut }
  } = useField('clockOut');

  const {
    input: { value: selectedOpenShiftId, onChange: onChangeOpenShiftId }
  } = useField('openShiftId');

  const {
    input: { value: notesValue, onChange: onChangeNotes }
  } = useField('notes');

  const showClockedTotal = !!clockInValue && !!clockOutValue;

  const timesheetDataExists = clockInValue || clockOutValue || notesValue;

  const isCopyShiftDisabled = !enableCopyShift;

  const resetTimesheetData = useCallback(() => {
    onChangeClockIn(null);
    onChangeClockOut(null);
    onChangeNotes(null);
  }, [onChangeClockIn, onChangeClockOut, onChangeNotes]);

  const setFields = useCallback(
    ({ labelId, startTime, endTime, locationId: inputLocationId }) => {
      labelId !== undefined && onChangeLabelId(labelId);
      startTime !== undefined && onChangeStartTime(startTime);
      endTime !== undefined && onChangeEndTime(endTime);
      inputLocationId !== undefined && setLocationIdWithAreaReset(inputLocationId);
    },
    [onChangeEndTime, onChangeLabelId, onChangeStartTime, setLocationIdWithAreaReset]
  );

  const onChangeLabelDetails = useCallback(
    ({ labelId, labelStartTimeProp, labelEndTimeProp }) => {
      if (!labelId) {
        setFields({
          labelId: null,
          startTime: initialTimingValues?.startTime,
          endTime: initialTimingValues?.endTime,
          locationId: null
        });
        return;
      }

      const { startTime: labelStartTime, endTime: labelEndTime, locationId: labelLocationId } =
        mappedLabels?.[labelId] || [];

      const labelStartTimeWithDate = `${moment(dateValue).format(API_DATE_FORMAT)}, ${labelStartTime}`;
      const labelEndTimeWithDate = `${moment(dateValue).format(API_DATE_FORMAT)}, ${labelEndTime}`;

      const { startTime, endTime } = getTimeRangeWithDates({
        date: moment(dateValue).format(API_DATE_FORMAT),
        startTime: labelStartTimeProp || labelStartTimeWithDate || initialTimingValues?.startTime,
        endTime: labelEndTimeProp || labelEndTimeWithDate || initialTimingValues?.endTime
      });

      setFields({
        labelId,
        startTime,
        endTime,
        locationId: labelLocationId
      });
    },
    [dateValue, initialTimingValues?.endTime, initialTimingValues?.startTime, mappedLabels, setFields]
  );

  const openShiftsByDayOptions = useMemo(
    () =>
      (openShiftsByCurrentDate || []).map((option) => {
        const { label, id, position, startTime, endTime } = option;

        return {
          value: id,
          label: (
            <>
              <PrimaryLabel $color={label?.color}>{label?.title || ''}</PrimaryLabel>
              {`- ${position.slug} - ${moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat)}${
                endTime ? `- ${moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)}` : ''
              } `}
            </>
          )
        };
      }),
    [openShiftsByCurrentDate, timeFormat]
  );

  const openShiftsByID = openShiftsByCurrentDate.reduce((all, shift) => {
    all[shift.id] = shift;
    return all;
  }, {});

  const onSelectOpenShift = useCallback(
    (openShiftId) => {
      const { startTime, endTime, positionId } = openShiftsByID[openShiftId] || {};
      onChangeOpenShiftId(openShiftId);
      setIsCreateModalOpen(false);
      onChangeStartTime(startTime);
      onChangeEndTime(endTime);
      setPositionId(positionId);
    },
    [onChangeEndTime, onChangeOpenShiftId, onChangeStartTime, openShiftsByID, setIsCreateModalOpen, setPositionId]
  );

  useEffect(() => {
    (openShiftsByDayOptions?.length === 0 || editMode) && setIsCreateModalOpen(true);
  }, [editMode, openShiftsByDayOptions?.length, setIsCreateModalOpen]);

  useEffect(() => {
    if (isCreateModalOpen) {
      onChangeOpenShiftId(null);
    }
  }, [isCreateModalOpen, onChangeOpenShiftId]);

  return (
    <Wrapper>
      {editMode && !isWeeklyPlanner && (
        <AttentionBox>
          <AttentionBoxContent>
            <InfoText>{t('shiftIsPartOfEvent')}</InfoText>
            <SeeEventLink onClick={handleSeeEvent}>{t('seeEvent')}</SeeEventLink>
          </AttentionBoxContent>
        </AttentionBox>
      )}

      {openShiftsByDayOptions?.length > 0 && !editMode ? (
        <OpenShiftsSectionComponent
          showLabel={showLabel}
          isCreateModalOpen={isCreateModalOpen}
          openShiftsByDayOptions={openShiftsByDayOptions}
          selectedOpenShiftId={selectedOpenShiftId}
          onSelectOpenShift={onSelectOpenShift}
          openShiftsByID={openShiftsByID}
        />
      ) : (
        <></>
      )}

      {isCreateModalOpen && (
        <NewShiftSection>
          {openShiftsByDayOptions?.length > 0 && !editMode && <SectionTitle>{t('addNewShift')}</SectionTitle>}

          {!isCopyShiftDisabled && (
            <HourlyCopyDayWrapper>
              <HourlyCopyDay
                startDate={startDate}
                endDate={endDate}
                branchId={branchId}
                selectedDays={selectedDays}
                setSelectedDays={setSelectedDays}
              />
            </HourlyCopyDayWrapper>
          )}

          <GridContainer>
            <GridItem>
              <PositionsField
                title={t('position')}
                name="positionId"
                onAdd={setPositionId}
                validate={isPositionRequired ? required : undefined}
                required={isPositionRequired}
                clearable={!isPositionRequired}
                branchId={branchId}
              />
            </GridItem>

            <GridItem>
              {showLabelField && (
                <LabelsSelectField
                  title={t('isShiftType')}
                  name="labelId"
                  isDisabled={editMode}
                  onChangeExternal={onChangeLabelDetails}
                  clearable={!editMode}
                />
              )}
            </GridItem>

            <GridItem>
              <LocationSelectField
                isLoading={isLoadingLocationField}
                customLocationsOptions={customLocationsOptions}
                onChange={onChangeLocation}
                validate={required}
                value={locationId}
                name="locationId"
                disabled={editMode}
                //todo
                // isDisabled={
                //   disableEditingExistingClient &&
                //   mappedClientBranches[clientBranchIdValue]?.status !== ACCOUNT_BILLING_STATUS_MAP.CLIENT_FREEMIUM
                // }
                error={locationErrors?.locationId}
                getOptionSpecificStyle={getLocationOptionSpecificStyle}
                required
                checkCloseUponOptionSelectFunc={checkCloseUponLocationOptionSelectFunc}
              />
            </GridItem>

            <GridItem>
              <LocationSelectField
                name="areaId"
                title={t('shiftArea')}
                value={areaDisplayValue}
                onChange={onChangeArea}
                branchId={branchId}
                isLoading={isLoadingAreaField}
                customLocationsOptions={areaLocationsOptions}
                getOptionSpecificStyle={getAreaOptionSpecificStyle}
                checkCloseUponOptionSelectFunc={checkCloseUponAreaOptionSelectFunc}
                allowAdd={false}
                clearable
                isDisabled={editMode || !locationId}
              />
            </GridItem>

            <GridItem>
              <HourlyBookingGeneralTimeFields
                startTimeFieldName="startTime"
                endTimeFieldName="endTime"
                showLabelField={showLabelField}
                timePickerOptions={timePickerOptions}
              />
            </GridItem>

            <GridItem>
              <CostCenterSection
                name="branchCostCenterId"
                required={isCostCenterRequired}
                validate={isCostCenterRequired ? required : null}
              />
            </GridItem>
          </GridContainer>

          <ClientSelectWrapper>
            <ClientSelectField
              disableAddNewClient //todo
              required={isClientRequired}
              validate={isClientRequired ? required : undefined}
              isDisabled={disableEditingExistingClient} //todo
              onChangeClientAdmin={setClientAdminId}
              disableClientAdminEdit={disableEditingExistingClient}
              onChangeClientBranchId={onChangeClientBranchId}
            />
          </ClientSelectWrapper>

          {showShiftTags && <ShiftTagsField eventTags={eventTags} />}

          <AssigningSectionWrapper>
            <GridContainer>
              <GridItem>
                <Label title={assignedToLabel}>
                  {employee ? (
                    <UserCardWrapper>
                      <UserCard
                        user={employee}
                        shouldRenderWarningBorder={employeeWarnings?.shouldRenderWarningBorder}
                        hasWarnings={employeeWarnings?.hasWarnings}
                        actions={
                          <div className="editable">
                            <CircleIcon
                              icon={RemoveIcon}
                              tooltip={t('bookingRemoveTooltip')}
                              onClick={() => setEmployee(null)}
                            />
                          </div>
                        }
                      />
                      <EmployeeBookingWarningsRenderer
                        slotWarnings={activeSlotWarnings}
                        externalFormattedWarnings={activeExternalWarnings}
                        projectId={projectId}
                        shiftId={shiftId}
                        branchId={branchId}
                        user={employee}
                        age={employeeAge}
                      />
                    </UserCardWrapper>
                  ) : (
                    <AssignToField
                      resetTimesheetData={resetTimesheetData}
                      setAddEmployeeModal={setAddEmployeeModal}
                      branchId={branchId}
                      timeFormat={timeFormat}
                      editMode={editMode}
                      startTime={startTimeValue}
                      setEmployee={setEmployee}
                      endTime={endTimeValue}
                      positionId={selectedPositionId}
                      shiftId={shiftId ?? selectedOpenShiftId}
                      projectId={projectId}
                    />
                  )}
                </Label>
              </GridItem>
            </GridContainer>
          </AssigningSectionWrapper>

          {editMode && employee && (
            <Accordion
              className="booking-accordion"
              defaultOpenRows={{ 0: editMode && employee && timesheetDataExists }}>
              <Accordion.Item title={t('actualShiftHours')}>
                <FlexColumn>
                  <GridContainer>
                    <GridItem>
                      <Header3SemiBold>{t('reportedHours')}</Header3SemiBold>

                      <FieldSection>
                        <HourlyBookingGeneralTimeFields
                          startTimeFieldName="clockIn"
                          defaultStartValue={clockInValue || startTimeValue}
                          endTimeFieldName="clockOut"
                          defaultEndValue={clockOutValue || endTimeValue}
                          showLabelField={showLabelField}
                          timePickerOptions={timePickerOptions}
                          startPlaceholder={t('start')}
                          endPlaceholder={t('end')}
                          hasTotalTime={!!showClockedTotal}
                          readOnly={disableTimesChange}
                        />

                        <TextAreaField title={t('notes')} name="notes" placeholder={t('addNotes')} />
                      </FieldSection>
                    </GridItem>
                  </GridContainer>

                  <BreaksTableWrapper>
                    <SemiBoldTitle>{t('reportedBreaks')}</SemiBoldTitle>

                    {clockInValue && clockOutValue ? (
                      <BreaksTable
                        branchId={branchId}
                        showTitle={false}
                        date={moment().format(API_DATE_FORMAT)}
                        startTime={clockInValue}
                        endTime={clockOutValue}
                        readOnly={disableTimesChange}
                      />
                    ) : (
                      <BreaksEmptyState> ({t('breaksTableEmptyStateDescription')})</BreaksEmptyState>
                    )}
                  </BreaksTableWrapper>
                </FlexColumn>
              </Accordion.Item>
            </Accordion>
          )}
        </NewShiftSection>
      )}
    </Wrapper>
  );
};

export default React.memo(HourlyBookingFields);
